/**
 * Pure helpers for cross-view sync (map <-> section <-> 3D). No React, no three.js.
 *
 * sectionLine: { coordinates: [[lon,lat],...], modelId?, name?, xRange?: [x0,x1] }
 *   `xRange` maps the polyline onto the section's own x axis (model.xRange); without it
 *   x is metres along the polyline.
 * hovered: { source: 'map'|'section'|'3d', modelId, x, depth, lonLat }
 */
const M_LAT = 110540;
const mLon = (lat0) => 111320 * Math.cos((lat0 * Math.PI) / 180);

/** Local tangent plane (metres): x east, z north, around origin { lon0, lat0 }. */
export function lonLatToLocal([lon, lat], origin) {
  return [(lon - origin.lon0) * mLon(origin.lat0), (lat - origin.lat0) * M_LAT];
}
export function localToLonLat(x, z, origin) {
  return [origin.lon0 + x / mLon(origin.lat0), origin.lat0 + z / M_LAT];
}

function polylineLocal(coords) {
  const lat0 = coords.reduce((s, c) => s + c[1], 0) / coords.length;
  const lon0 = coords.reduce((s, c) => s + c[0], 0) / coords.length;
  const origin = { lon0, lat0 };
  const pts = coords.map((c) => lonLatToLocal(c, origin));
  const cum = [0];
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  return { origin, pts, cum, total: cum[cum.length - 1] };
}

/** Length of a lon/lat polyline in metres (local tangent-plane approximation). */
export function lineLengthM(coords) {
  return coords && coords.length >= 2 ? polylineLocal(coords).total : 0;
}

/**
 * Project a lon/lat onto a polyline. Returns { frac 0..1, along (m), offset (m, perpendicular
 * distance), snapped [lon,lat], total (m) } or null for a degenerate line.
 */
export function projectOnLine(coords, lonLat) {
  if (!coords || coords.length < 2 || !lonLat) return null;
  const { origin, pts, cum, total } = polylineLocal(coords);
  if (!(total > 0)) return null;
  const p = lonLatToLocal(lonLat, origin);
  let best = null;
  for (let i = 1; i < pts.length; i++) {
    const [ax, az] = pts[i - 1], [bx, bz] = pts[i];
    const dx = bx - ax, dz = bz - az;
    const len2 = dx * dx + dz * dz;
    if (!len2) continue;
    const t = Math.max(0, Math.min(1, ((p[0] - ax) * dx + (p[1] - az) * dz) / len2));
    const sx = ax + dx * t, sz = az + dz * t;
    const offset = Math.hypot(p[0] - sx, p[1] - sz);
    if (!best || offset < best.offset) best = { offset, along: cum[i - 1] + t * Math.sqrt(len2), sx, sz };
  }
  if (!best) return null;
  return { frac: best.along / total, along: best.along, offset: best.offset, snapped: localToLonLat(best.sx, best.sz, origin), total };
}

/** Point on a polyline at fraction 0..1 of its length -> [lon, lat]. */
export function lonLatAtFrac(coords, frac) {
  if (!coords || coords.length < 2) return null;
  const { origin, pts, cum, total } = polylineLocal(coords);
  if (!(total > 0)) return coords[0];
  const d = Math.max(0, Math.min(1, frac)) * total;
  let i = 1;
  while (i < cum.length - 1 && cum[i] < d) i++;
  const seg = cum[i] - cum[i - 1] || 1;
  const t = (d - cum[i - 1]) / seg;
  return localToLonLat(pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * t, pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * t, origin);
}

/** Section x coordinate for a fraction along the line. */
export function xFromFrac(sectionLine, frac) {
  if (sectionLine?.xRange) return sectionLine.xRange[0] + frac * (sectionLine.xRange[1] - sectionLine.xRange[0]);
  return frac * lineLengthM(sectionLine?.coordinates);
}
export function fracFromX(sectionLine, x) {
  if (sectionLine?.xRange) {
    const span = sectionLine.xRange[1] - sectionLine.xRange[0];
    return span ? (x - sectionLine.xRange[0]) / span : 0;
  }
  const total = lineLengthM(sectionLine?.coordinates);
  return total ? x / total : 0;
}

/** Fill in `lonLat` from `x` (section/3D hovers) when a section line is known. */
export function resolveHover(hover, sectionLine) {
  if (!hover) return null;
  if (hover.lonLat || typeof hover.x !== 'number' || !sectionLine?.coordinates) return hover;
  const lonLat = lonLatAtFrac(sectionLine.coordinates, fracFromX(sectionLine, hover.x));
  return lonLat ? { ...hover, lonLat } : hover;
}

export function sameHover(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const ll = (h) => (h.lonLat ? `${h.lonLat[0].toFixed(7)},${h.lonLat[1].toFixed(7)}` : '');
  return a.source === b.source && a.modelId === b.modelId && a.x === b.x && a.depth === b.depth && ll(a) === ll(b);
}

/** GeoJSON for the map: the section line, and the hover marker (when it comes from another view). */
export function sectionLineGeoJSON(sectionLine) {
  const c = sectionLine?.coordinates;
  return {
    type: 'FeatureCollection',
    features: c && c.length >= 2
      ? [{ type: 'Feature', properties: { name: sectionLine.name || '' }, geometry: { type: 'LineString', coordinates: c } }]
      : [],
  };
}
export function hoverGeoJSON(hovered, self = 'map') {
  const ok = hovered && hovered.source !== self && hovered.lonLat;
  return {
    type: 'FeatureCollection',
    features: ok ? [{ type: 'Feature', properties: { source: hovered.source }, geometry: { type: 'Point', coordinates: hovered.lonLat } }] : [],
  };
}

/** Distance in metres between two lon/lat points (for echo suppression). */
export function lonLatDistanceM(a, b) {
  if (!a || !b) return Infinity;
  const lat0 = (a[1] + b[1]) / 2;
  return Math.hypot((a[0] - b[0]) * mLon(lat0), (a[1] - b[1]) * M_LAT);
}

/** A synced camera target further than this from every model falls back to fitting the models. */
export const SYNC_FAR_M = 2000;

/** Lon/lat bounding box { minLon, maxLon, minLat, maxLat } of models (fence line ends, voxel cell lon/lat), or null. */
export function modelsExtentLonLat(models) {
  let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
  const add = (lon, lat) => {
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    minLon = Math.min(minLon, lon); maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
  };
  (models || []).forEach((m) => {
    if (m?.line?.length === 2) m.line.forEach(([lon, lat]) => add(lon, lat));
    else if (m?.cells?.[0]?.lon !== undefined) m.cells.forEach((c) => add(c.lon, c.lat));
  });
  return Number.isFinite(minLon) ? { minLon, maxLon, minLat, maxLat } : null;
}

/** Distance (m) from a lon/lat point to a lon/lat bounding box; 0 inside it. */
export function distanceToExtentM(lonLat, extent) {
  if (!extent || !lonLat) return Infinity;
  const clamped = [Math.min(Math.max(lonLat[0], extent.minLon), extent.maxLon), Math.min(Math.max(lonLat[1], extent.minLat), extent.maxLat)];
  return lonLatDistanceM(lonLat, clamped);
}

/**
 * What the 3D view does with a camera target synced from the map: 'follow' it, or 'fit' the
 * models when it is further than `farM` from their extent (the scene would open empty). Without
 * a model extent there is nothing to fit, so it follows.
 */
export function syncTargetDecision(lonLat, extent, farM = SYNC_FAR_M) {
  if (!extent || !lonLat) return 'follow';
  return distanceToExtentM(lonLat, extent) > farM ? 'fit' : 'follow';
}
